import React, { useState, useEffect } from 'react';
import { formatDate, toISODate } from '../../utils/date';
import { motion } from 'framer-motion';
import { 
  UserIcon, 
  CalendarDaysIcon, 
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { H2, H3, Body, Button, Badge, Card } from '../ui';
import { Input, Select, TextArea } from '../forms';
import { useChildren, useAssessmentScales, useLearningAreas, useLearningOutcomes } from '../../hooks/api';
import { AssessmentCreate, AssessmentUpdate, Assessment, LearningArea, LearningOutcome, AssessmentScale } from '../../types/api';

interface EnhancedAssessmentFormProps {
  assessment?: Assessment;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  defaultChildId?: string;
}

interface FormData {
  child_id: string;
  learning_area_id: string;
  learning_outcome_id: string;
  assessment_scale_id: string;
  assessment_date: string;
  score: string;
  notes: string;
}

const EnhancedAssessmentForm: React.FC<EnhancedAssessmentFormProps> = ({
  assessment,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
  defaultChildId,
}) => {
  const { data: childrenData } = useChildren();
  const { data: learningAreas } = useLearningAreas();
  const { data: assessmentScales } = useAssessmentScales();
  const children = childrenData?.items || [];
  
  const [formData, setFormData] = useState<FormData>({
    child_id: assessment?.child_id || defaultChildId || '',
    learning_area_id: assessment?.learning_area_id || '',
    learning_outcome_id: assessment?.learning_outcome_id || '',
    assessment_scale_id: assessment?.assessment_scale_id || '',
    assessment_date: assessment?.assessment_date
      ? toISODate(new Date(assessment.assessment_date))
      : toISODate(),
    score: assessment?.score?.toString() || '',
    notes: assessment?.notes || '',
  });
  
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [currentStep, setCurrentStep] = useState<'basic' | 'scoring' | 'notes'>('basic');
  const [selectedLearningArea, setSelectedLearningArea] = useState<LearningArea | null>(null);
  const [selectedScale, setSelectedScale] = useState<AssessmentScale | null>(null);
  
  // Fetch learning outcomes based on selected learning area
  const { data: learningOutcomes } = useLearningOutcomes({
    learning_area_id: formData.learning_area_id,
  }, !!formData.learning_area_id);

  // Get selected child details
  const selectedChild = children.find(child => child.id === formData.child_id);

  useEffect(() => {
    if (assessment) {
      setFormData({
        child_id: assessment.child_id,
        learning_area_id: assessment.learning_area_id,
        learning_outcome_id: assessment.learning_outcome_id,
        assessment_scale_id: assessment.assessment_scale_id,
        assessment_date: toISODate(new Date(assessment.assessment_date)),
        score: assessment.score?.toString() || '',
        notes: assessment.notes || '',
      });
    }
  }, [assessment]);

  useEffect(() => {
    if (formData.learning_area_id && learningAreas) {
      const area = learningAreas.find(a => a.id === formData.learning_area_id);
      setSelectedLearningArea(area || null);
    }
  }, [formData.learning_area_id, learningAreas]);

  useEffect(() => {
    if (formData.assessment_scale_id && assessmentScales) {
      const scale = assessmentScales.find(s => s.id === formData.assessment_scale_id);
      setSelectedScale(scale || null);
    }
  }, [formData.assessment_scale_id, assessmentScales]);

  // Reset learning outcome when learning area changes
  useEffect(() => {
    if (mode === 'create' && formData.learning_area_id) {
      setFormData(prev => ({ ...prev, learning_outcome_id: '' }));
    }
  }, [formData.learning_area_id, mode]);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.child_id) {
      newErrors.child_id = 'Please select a child';
    }
    
    if (!formData.learning_area_id) {
      newErrors.learning_area_id = 'Please select a learning area';
    }
    
    if (!formData.learning_outcome_id) {
      newErrors.learning_outcome_id = 'Please select a learning outcome';
    }
    
    if (!formData.assessment_scale_id) {
      newErrors.assessment_scale_id = 'Please select an assessment scale';
    }
    
    if (!formData.assessment_date) {
      newErrors.assessment_date = 'Please select an assessment date';
    }
    
    // Validate score if provided
    if (formData.score && selectedScale) {
      const scoreNum = Number(formData.score);
      if (isNaN(scoreNum)) {
        newErrors.score = 'Score must be a number';
      } else if (selectedScale.min_value !== undefined && selectedScale.max_value !== undefined && 
                 (scoreNum < selectedScale.min_value || scoreNum > selectedScale.max_value)) {
        newErrors.score = `Score must be between ${selectedScale.min_value} and ${selectedScale.max_value}`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submissionData: any = {
      learning_area_id: formData.learning_area_id,
      learning_outcome_id: formData.learning_outcome_id,
      assessment_scale_id: formData.assessment_scale_id,
      assessment_date: formData.assessment_date,
      score: formData.score ? Number(formData.score) : undefined,
      notes: formData.notes.trim() || undefined,
    };
    
    if (mode === 'create') {
      submissionData.child_id = formData.child_id;
    }
    
    onSubmit(submissionData);
  };

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getScaleDescriptors = () => {
    if (!selectedScale?.scale_descriptors) return null;
    
    try {
      return JSON.parse(selectedScale.scale_descriptors);
    } catch {
      return null;
    }
  };

  const getScoreIndicator = () => {
    if (!formData.score || !selectedScale) return null;
    
    const score = Number(formData.score);
    if (isNaN(score)) return null;
    
    const { min_value, max_value } = selectedScale;
    if (min_value === undefined || max_value === undefined) return null;
    
    const range = max_value - min_value;
    const percentage = ((score - min_value) / range) * 100;
    
    let variant: 'success' | 'warning' | 'error' | 'info' = 'info';
    let label = 'Within Range';
    
    if (percentage >= 80) {
      variant = 'success';
      label = 'Excellent';
    } else if (percentage >= 60) {
      variant = 'info';
      label = 'Good';
    } else if (percentage >= 40) {
      variant = 'warning';
      label = 'Developing';
    } else {
      variant = 'error';
      label = 'Needs Support';
    }
    
    return { variant, label, percentage };
  };

  const steps = [
    { id: 'basic', label: 'Basic Information', icon: UserIcon },
    { id: 'scoring', label: 'Assessment & Scoring', icon: ChartBarIcon },
    { id: 'notes', label: 'Notes & Comments', icon: ClipboardDocumentCheckIcon },
  ];

  const scoreIndicator = getScoreIndicator();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <H2>{mode === 'create' ? 'Create New Assessment' : 'Edit Assessment'}</H2>
        <Body className="text-gray-600 mt-1">
          {mode === 'create' 
            ? 'Create a detailed assessment record for a child\'s learning outcome'
            : 'Update the assessment details and scoring'
          }
        </Body>
      </div>

      {/* Step Navigation */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        {steps.map((step, index) => (
          <button
            key={step.id}
            type="button"
            onClick={() => setCurrentStep(step.id as any)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              currentStep === step.id
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <step.icon className="w-5 h-5" />
            <span className="font-medium">{step.label}</span>
          </button>
        ))}
      </div>

      {/* Step Content */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {currentStep === 'basic' && (
          <div className="space-y-6">
            {/* Child and Date Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Child <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.child_id}
                  onChange={(e) => handleChange('child_id', e.target.value)}
                  options={[
                    { value: '', label: 'Select a child' },
                    ...children.map(child => ({
                      value: child.id,
                      label: `${child.name} ${child.surname}`,
                    }))
                  ]}
                  error={errors.child_id}
                  disabled={mode === 'edit'}
                />
                {selectedChild && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                    <Body className="text-sm text-gray-600">
                      Age: {selectedChild.age} • Gender: {selectedChild.gender === 'M' ? 'Male' : 'Female'}
                    </Body>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                  Assessment Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={formData.assessment_date}
                  onChange={(e) => handleChange('assessment_date', e.target.value)}
                  error={errors.assessment_date}
                  max={toISODate()}
                />
              </div>
            </div>

            {/* Learning Area Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <AcademicCapIcon className="w-4 h-4 inline mr-1" />
                Learning Area <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {learningAreas?.map((area) => (
                  <Card
                    key={area.id}
                    className={`p-4 cursor-pointer transition-all ${
                      formData.learning_area_id === area.id
                        ? 'ring-2 ring-primary-500 border-primary-200'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => handleChange('learning_area_id', area.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{area.name}</span>
                      <Badge variant="secondary" size="sm">
                        {area.code}
                      </Badge>
                    </div>
                    {area.description && (
                      <Body className="text-sm text-gray-600">
                        {area.description}
                      </Body>
                    )}
                  </Card>
                ))}
              </div>
              {errors.learning_area_id && (
                <p className="mt-1 text-sm text-red-600">{errors.learning_area_id}</p>
              )}
            </div>

            {/* Learning Outcome Selection */}
            {formData.learning_area_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Learning Outcome <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {learningOutcomes?.map((outcome) => (
                    <Card
                      key={outcome.id}
                      className={`p-3 cursor-pointer transition-all ${
                        formData.learning_outcome_id === outcome.id
                          ? 'ring-2 ring-primary-500 border-primary-200'
                          : 'hover:shadow-sm'
                      }`}
                      onClick={() => handleChange('learning_outcome_id', outcome.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <Badge variant="info" size="sm">
                              {outcome.code}
                            </Badge>
                            <Body className="font-medium">
                              {outcome.short_description || outcome.description}
                            </Body>
                          </div>
                          {outcome.short_description && (
                            <Body className="text-sm text-gray-600 mt-1">
                              {outcome.description}
                            </Body>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                {errors.learning_outcome_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.learning_outcome_id}</p>
                )}
              </div>
            )}
          </div>
        )}

        {currentStep === 'scoring' && (
          <div className="space-y-6">
            {/* Assessment Scale Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <ChartBarIcon className="w-4 h-4 inline mr-1" />
                Assessment Scale <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assessmentScales?.map((scale) => (
                  <Card
                    key={scale.id}
                    className={`p-4 cursor-pointer transition-all ${
                      formData.assessment_scale_id === scale.id
                        ? 'ring-2 ring-primary-500 border-primary-200'
                        : 'hover:shadow-md'
                    }`}
                    onClick={() => handleChange('assessment_scale_id', scale.id)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{scale.name}</span>
                      <Badge variant="secondary" size="sm">
                        {scale.scale_type}
                      </Badge>
                    </div>
                    {scale.min_value !== undefined && scale.max_value !== undefined && (
                      <Body className="text-sm text-gray-600">
                        Range: {scale.min_value} - {scale.max_value}
                      </Body>
                    )}
                    {scale.report_blurb && (
                      <Body className="text-sm text-gray-600 mt-1">
                        {scale.report_blurb}
                      </Body>
                    )}
                  </Card>
                ))}
              </div>
              {errors.assessment_scale_id && (
                <p className="mt-1 text-sm text-red-600">{errors.assessment_scale_id}</p>
              )}
            </div>

            {/* Score Input */}
            {selectedScale && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Score
                  {selectedScale.min_value !== undefined && selectedScale.max_value !== undefined && (
                    <span className="text-sm text-gray-500 ml-1">
                      (Range: {selectedScale.min_value} - {selectedScale.max_value})
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Input
                      type="number"
                      value={formData.score}
                      onChange={(e) => handleChange('score', e.target.value)}
                      placeholder="Enter score (optional)"
                      error={errors.score}
                      min={selectedScale.min_value}
                      max={selectedScale.max_value}
                      step={selectedScale.scale_type === 'numeric' ? '0.1' : '1'}
                    />
                  </div>
                  {scoreIndicator && (
                    <div className="flex items-center space-x-3">
                      <Badge variant={scoreIndicator.variant as any} size="sm">
                        {scoreIndicator.label}
                      </Badge>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            scoreIndicator.variant === 'success' ? 'bg-green-500' :
                            scoreIndicator.variant === 'info' ? 'bg-blue-500' :
                            scoreIndicator.variant === 'warning' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${scoreIndicator.percentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scale Descriptors */}
            {getScaleDescriptors() && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                  <H3 className="text-sm font-medium text-blue-900">Scale Guide</H3>
                </div>
                <div className="space-y-2">
                  {Object.entries(getScaleDescriptors()).map(([key, value]) => (
                    <div key={key} className="flex items-start space-x-2">
                      <Badge variant="info" size="sm">
                        {key}
                      </Badge>
                      <Body className="text-sm text-blue-800">
                        {value as string}
                      </Body>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'notes' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ClipboardDocumentCheckIcon className="w-4 h-4 inline mr-1" />
                Assessment Notes
              </label>
              <TextArea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Add detailed notes about this assessment, including observations, context, recommendations, or next steps..."
                rows={6}
                helperText={`${formData.notes.length}/1000 characters`}
                maxLength={1000}
              />
            </div>

            {/* Assessment Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <H3 className="font-medium text-gray-900 mb-3">Assessment Summary</H3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Child:</span>
                  <span className="text-sm font-medium">
                    {selectedChild ? `${selectedChild.name} ${selectedChild.surname}` : 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Learning Area:</span>
                  <span className="text-sm font-medium">
                    {selectedLearningArea ? `${selectedLearningArea.code} - ${selectedLearningArea.name}` : 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Assessment Date:</span>
                  <span className="text-sm font-medium">
                    {formData.assessment_date ? formatDate(formData.assessment_date) : 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Scale:</span>
                  <span className="text-sm font-medium">
                    {selectedScale ? `${selectedScale.name} (${selectedScale.scale_type})` : 'Not selected'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Score:</span>
                  <span className="text-sm font-medium">
                    {formData.score || 'Not scored'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <div className="flex space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          {currentStep !== 'basic' && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                const stepIndex = steps.findIndex(s => s.id === currentStep);
                if (stepIndex > 0) {
                  setCurrentStep(steps[stepIndex - 1].id as any);
                }
              }}
              disabled={isLoading}
            >
              Previous
            </Button>
          )}
        </div>
        
        <div className="flex space-x-3">
          {currentStep !== 'notes' && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const stepIndex = steps.findIndex(s => s.id === currentStep);
                if (stepIndex < steps.length - 1) {
                  setCurrentStep(steps[stepIndex + 1].id as any);
                }
              }}
              disabled={isLoading}
            >
              Next
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {mode === 'create' ? 'Create Assessment' : 'Update Assessment'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EnhancedAssessmentForm;
